import { wcQry, wcQryAll, setHidden } from '../util/wcUtil.ts';
import { devlog } from '../util/utils.ts';
import { appData, resolveAppData } from '../data/projectManagement.ts';
import { params } from '../store/paramStore.ts';
import type { ParamAliases, ParamData, ResolvedParams } from '../store/paramStore.ts';
import { sysData } from '../store/sysDataStore.ts';
import type { SysData, SysDataKey } from '../store/sysDataStore.ts';

type Constructor<T = HTMLElement> = new (...args: any[]) => T & {
  connectedCallback?(): void;
  disconnectedCallback?(): void;
  attributeChangedCallback?(name: string, oldValue: string | null, newValue: string | null): void;
};

/**
 * The `attrs`/`classes`/`methods` mapping a syncWithProjData() `compute`
 * returns — one appData key (or several, sharing one compute) can drive any
 * number of attrs, classes and methods from a single binding.
 * - `attrs`: DOM attributes to reflect. `null`/`undefined` removes the
 *   attribute; anything else is set via `String(value)`. For a boolean
 *   toggle attribute, have compute return `''` for true / `null` for false.
 * - `classes`: class names to hold on or off, one per key — `{ hidden: true }`
 *   adds the class, `{ hidden: false }` removes it. Only the named classes are
 *   touched, so a class the markup or another binding put there survives. One
 *   name per key: `'a b'` is not a class name and the DOM rejects it.
 * - `methods`: instance methods to call, each with its mapped value (method
 *   name must be a real member of `Self`).
 */
export type ProjDataSyncTargets<Self = any> = {
  attrs?: Record<string, any>;
  classes?: Record<string, any>;
  methods?: Partial<Record<keyof Self & string, any>>;
};

/** Applies a computed `{ attrs, classes, methods }` target to any element —
 *  shared by AsElMixin's `syncWithProjData` and the standalone
 *  `bindElToProjData` below, so both dispatch identically. */
function applyProjDataTargets(el: HTMLElement, targets: ProjDataSyncTargets<any>): void {
  for (const [attr, value] of Object.entries(targets.attrs ?? {})) {
    if (value == null) el.removeAttribute(attr);
    else el.setAttribute(attr, String(value));
  }
  // After attrs: a compute that writes the whole `class` attribute and toggles
  // a single class in the same breath means the toggle, not the wholesale
  // write that would undo it.
  for (const [className, on] of Object.entries(targets.classes ?? {})) {
    el.classList.toggle(className, !!on);
  }
  for (const [method, value] of Object.entries(targets.methods ?? {})) {
    (el as any)[method]?.(value);
  }
}

/**
 * What every free binder takes as its element: the element itself, a CSS
 * selector to look it up with, or nothing at all. Passing nothing is a no-op
 * (see below), so a bare `document.querySelector(...)` result needs no `!`.
 *
 * A selector is resolved once, when the binding is made — the same as
 * `sendEv`. If the app replaces that markup later, rebind against the new
 * element; the binding does not go looking for it again.
 */
export type BindTarget = HTMLElement | string | null | undefined;

/** Options every free binder takes. */
export type BindElOpts = {
  /** Skip the dev warning when `el` is null — for markup that's legitimately
   *  absent in some modes, rather than a wrong selector. */
  failSilently?: boolean;
};

/**
 * What one bindable source has to answer for `bindElToSource` below. The three
 * sources differ only in these few lines each; everything else about binding an
 * element — the null guard and its warning, the attached `hide()`, applying
 * once up front, wiring the subscription — was the same code three times.
 */
type BindSource = {
  /** Function name used in the null-element warning. */
  label: string;
  /** Whether a bare function target means "the whole source". appData has no
   *  such form — its targets are always field names. */
  wholeTargetAllowed: boolean;
  /** The value `compute` receives. An undefined target means the whole source. */
  resolve(target: any): any;
  /** How to name the target in the warning. */
  describe(target: any): string;
  /** Wake `onChange` whenever the target's value could have moved. */
  subscribe(target: any, onChange: () => void): () => void;
};

const PROJ_DATA_SOURCE: BindSource = {
  label: 'bindElToProjData',
  wholeTargetAllowed: true,
  resolve: (target) => resolveAppData(target),
  describe: (target) => target === undefined ? 'all appData'
    : Array.isArray(target) ? target.join(', ')
    : String(target),
  // appData.subscribe defaults to coalesce: false — appData has always
  // recomputed once per changed key, and a multi-field bind that suddenly
  // fired once instead of twice would be a silent behaviour change in
  // existing apps. The other two sources coalesce.
  subscribe: (target, onChange) => target === undefined
    ? appData.subscribeAll(onChange)
    : appData.subscribe(target, onChange),
};

const PARAMS_SOURCE: BindSource = {
  label: 'bindElToParams',
  wholeTargetAllowed: true,
  resolve: (target) => target === undefined ? params.all()
    : typeof target === 'string' ? params.get(target)
    : Array.isArray(target) ? target.map(name => params.get(name))
    : params.resolve(target as ParamAliases),
  describe: (target) => target === undefined ? 'all params'
    : typeof target === 'string' ? target
    : Array.isArray(target) ? target.join(', ')
    : Object.values(target as ParamAliases).join(', '),
  subscribe: (target, onChange) => target === undefined
    ? params.subscribeAll(onChange)
    : (params.subscribe as any)(target, onChange),
};

const SYS_DATA_SOURCE: BindSource = {
  label: 'bindElToSysData',
  wholeTargetAllowed: true,
  resolve: (target) => target === undefined ? sysData.snapshot()
    : Array.isArray(target) ? target.map((key: SysDataKey) => (sysData as any)[key])
    : (sysData as any)[target as SysDataKey],
  describe: (target) => target === undefined ? 'all sysData'
    : Array.isArray(target) ? target.join(', ')
    : String(target),
  subscribe: (target, onChange) => target === undefined
    ? sysData.subscribeAll(onChange)
    : (sysData.subscribe as any)(target, onChange),
};

/**
 * The one binder. `compute` is always called as `compute(resolvedValue, target)`
 * and applied through `applyProjDataTargets`, so a single field gets
 * `(value, key)`, several get `(values, keys)`, and the whole-source form gets
 * `(snapshot, undefined)`.
 *
 * Applies immediately, then on every change. Returns the unsubscribe.
 */
function bindElToSource(
  source: BindSource,
  elOrSelector: BindTarget,
  targetOrCompute: any,
  computeOrOpts?: any,
  maybeOpts?: BindElOpts,
): () => void {
  // For a source with a whole-source form, a function in the target position
  // means there is no target: it is the compute, and the rest shifts along one.
  const whole = source.wholeTargetAllowed && typeof targetOrCompute === 'function';
  const target = whole ? undefined : targetOrCompute;
  const compute = whole ? targetOrCompute : computeOrOpts;
  const opts: BindElOpts = (whole ? computeOrOpts : maybeOpts) ?? {};

  const el = typeof elOrSelector === 'string'
    ? document.querySelector<HTMLElement>(elOrSelector)
    : elOrSelector;

  if (!el) {
    if (!opts.failSilently) {
      const where = typeof elOrSelector === 'string' ? ` matching "${elOrSelector}"` : '';
      devlog(`[${source.label}] no element${where} to bind — "${source.describe(target)}" will never apply. Wrong selector, or markup that isn't rendered? Pass { failSilently: true } if it's legitimately absent.`);
    }
    return () => {};
  }

  // A real AsEl already has one; a plain element gets it attached so
  // `methods: { hide }` works the same either way.
  if (!(el as any).hide) {
    (el as any).hide = (value: boolean) => setHidden(el, value);
  }

  const apply = () => applyProjDataTargets(el, compute(source.resolve(target), target));
  apply();
  return source.subscribe(target, apply);
}

/** Instance surface added by AsElMixin/AsEl — for merging into another
 *  component's own `interface Foo extends ...MixinMembers {}` declaration
 *  when it composes AsElMixin alongside other mixins instead of extending
 *  AsEl directly (see as-app.ts). */
export interface AsElMixinMembers {
  wcQry<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string): InstanceType<T> | null;
  wcQry(tag: string, selectorSuffix?: string): HTMLElement | null;
  wcQryAll<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string): InstanceType<T>[];
  wcQryAll(tag: string, selectorSuffix?: string): HTMLElement[];
  readonly typed: this;
  syncWithProjData(field: string, compute: (value: any, key: string) => ProjDataSyncTargets<this>): () => void;
  syncWithProjData(fields: string[], compute: (values: any[], keys: string[]) => ProjDataSyncTargets<this>): () => void;
  syncWithProjData(compute: (appData: Record<string, any>) => ProjDataSyncTargets<this>): () => void;
  bindHidden(field: string, hiddenWhen: (value: any) => boolean): () => void;
  bindHidden(fields: string[], hiddenWhen: (values: any[]) => boolean): () => void;
  bindAttrs(field: string, compute: (value: any) => Record<string, any>): () => void;
  bindAttrs(fields: string[], compute: (values: any[]) => Record<string, any>): () => void;
  bindParams(name: string, compute: (param: ParamData | undefined, name: string) => ProjDataSyncTargets<this>): () => void;
  bindParams<T extends ParamAliases>(aliases: T, compute: (params: ResolvedParams<T>) => ProjDataSyncTargets<this>): () => void;
  bindParams(compute: (params: Record<string, ParamData>) => ProjDataSyncTargets<this>): () => void;
  bindSysData<K extends SysDataKey>(key: K, compute: (value: SysData[K], key: K) => ProjDataSyncTargets<this>): () => void;
  bindSysData<K extends SysDataKey>(keys: readonly K[], compute: (values: Array<SysData[K]>, keys: readonly K[]) => ProjDataSyncTargets<this>): () => void;
  bindSysData(compute: (sys: Readonly<SysData>) => ProjDataSyncTargets<this>): () => void;
  hide(value: boolean): void;
}

/**
 * Mixin form of AsEl (below) — for components that need to compose it
 * alongside other mixins instead of extending AsEl directly, e.g.
 * `composeMixins(HTMLElement, ..., AsElMixin)` in as-app.ts. Same behavior;
 * see AsEl's docs for the full write-up.
 */
export function AsElMixin<TBase extends Constructor<HTMLElement>>(Base: TBase) {
  class WithAsEl extends Base {
    // Every subclass must set its own tag. No check for a missing one:
    // the browser already blocks it (see define() below).
    static readonly tag: string = '';

    // Observes the plain `hidden` attribute so setting it (the standard DOM
    // way to hide any element: `el.hidden = true` / `el.toggleAttribute('hidden', ...)`)
    // gets the same forceful !important override as calling hide() directly —
    // needed because a component's own shadow `:host { display: ... }` rule
    // would otherwise beat the browser's built-in `[hidden] { display: none }`.
    // A subclass with its own observedAttributes/attributeChangedCallback must
    // merge with these (`...super.observedAttributes`) and chain
    // (`super.attributeChangedCallback(...)`) to keep this working — see
    // as-text.ts / as-configurator.ts for the pattern.
    static get observedAttributes(): string[] {
      return ['hidden'];
    }

    // Chains to the base first: in as-app this mixin is the outermost one, and
    // HistoryMixin and FileDropMixin below it own attributes of their own.
    attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
      super.attributeChangedCallback?.(name, oldValue, newValue);
      if (name === 'hidden') this.hide(this.hasAttribute('hidden'));
    }

    // Every binding this element holds — appData (syncWithProjData), params
    // (bindParams) and sysData (bindSysData) alike. Emptied by
    // disconnectedCallback below.
    _bindUnbinds: Array<() => void> = [];

    // Turns a free binder's unsubscribe into an unbind this element also
    // cleans up on disconnect. Bare `_name` with no modifier: a mixin class
    // expression can't take `private` (TS4094, see CLAUDE.md).
    _trackBinding(unsubscribe: () => void): () => void {
      const unbind = () => {
        unsubscribe();
        this._bindUnbinds = this._bindUnbinds.filter(u => u !== unbind);
      };
      this._bindUnbinds.push(unbind);
      return unbind;
    }

    // No-op (beyond chaining) — exists so a subclass overriding
    // connectedCallback() can always call super.connectedCallback() safely.
    connectedCallback(): void {
      super.connectedCallback?.();
    }

    // Cleans up any syncWithProjData()/bindParams() subscriptions this element
    // still holds.
    // A subclass that overrides disconnectedCallback() MUST call
    // super.disconnectedCallback() (anywhere in its own override) or this
    // never runs.
    disconnectedCallback(): void {
      super.disconnectedCallback?.();
      this._bindUnbinds.forEach(unbind => unbind());
      this._bindUnbinds = [];
    }

    /** Registers the class under its tag. Same as the manual
     *  if-not-defined-else-warn block every component used to hand-write. */
    static define(this: CustomElementConstructor & typeof WithAsEl): void {
      if (customElements.get(this.tag)) {
        console.warn(`${this.tag || '(AsEl subclass with no tag set)'} already defined`);
        return;
      }
      customElements.define(this.tag, this);
    }

    /** Same as wcQry(), but only searches inside this element. */
    wcQry<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string): InstanceType<T> | null;
    wcQry(tag: string, selectorSuffix?: string): HTMLElement | null;
    wcQry(ctorOrTag: CustomElementConstructor | string, selectorSuffix = ''): HTMLElement | null {
      return wcQry(ctorOrTag as any, selectorSuffix, this);
    }

    /** Same as wcQryAll(), but only searches inside this element. */
    wcQryAll<T extends CustomElementConstructor>(ctor: T, selectorSuffix?: string): InstanceType<T>[];
    wcQryAll(tag: string, selectorSuffix?: string): HTMLElement[];
    wcQryAll(ctorOrTag: CustomElementConstructor | string, selectorSuffix = ''): HTMLElement[] {
      return wcQryAll(ctorOrTag as any, selectorSuffix, this);
    }

    /**
     * Force-hides (or reveals) this element via an inline `display` override
     * (`!important`), the same technique used to hide it out from under a
     * component's own `:host { display: ... }` rule — inline style always
     * wins over an external stylesheet short of that rule also being
     * `!important`. Usable directly as a syncWithProjData method target:
     *   this.syncWithProjData('advancedMode', (v) => ({ methods: { hide: !v } }));
     */
    hide(value: boolean): void {
      setHidden(this, value);
    }

    /** Returns `this`. Logs (doesn't throw) if the real tagName doesn't match
     *  the class's declared tag — a sign this reference was wrongly assumed
     *  to be this type. */
    get typed(): this {
      const expected = (this.constructor as typeof WithAsEl).tag;
      if (expected && this.tagName.toLowerCase() !== expected) {
        devlog(`[AsEl.typed] <${this.tagName.toLowerCase()}> is not <${expected}> (declared on ${this.constructor.name})`);
      }
      return this;
    }

    /**
     * Syncs currentProjectData.appData onto this element. Call it
     * whenever you want (usually connectedCallback()) — cleanup is
     * automatic, via disconnectedCallback() above, as long as an overridden
     * disconnectedCallback() calls super.disconnectedCallback(). The
     * returned function also unbinds early if you need that.
     *
     * `compute` runs once immediately with the current value(s), then again
     * on every later change to any bound key, and returns the attrs, classes
     * and methods to apply for that value — one key (or several, given as an
     * array, e.g. a combined x/y position) can drive any number of them from a
     * single binding. Call it multiple times for independent keys.
     *
     *   class MyEl extends AsEl {
     *     connectedCallback() {
     *       super.connectedCallback();
     *       this.syncWithProjData('note', (value) => ({ attrs: { note: value } }));
     *       this.syncWithProjData('shapeCount', (n) => ({
     *         classes: { 'has-shapes': n > 0, crowded: n > 20 },
     *       }));
     *       this.syncWithProjData('cropScale', (value) => ({
     *         methods: { applyCropScale: value ?? 1 },
     *       }));
     *       this.syncWithProjData(['x', 'y'], ([x, y]) => ({
     *         methods: { setPosition: { x, y } },
     *       }));
     *     }
     *     disconnectedCallback() { super.disconnectedCallback(); }
     *     applyCropScale(scale: number) { ... }
     *     setPosition(pos: { x: number; y: number }) { ... }
     *   }
     */
    syncWithProjData(field: string, compute: (value: any, key: string) => ProjDataSyncTargets<this>): () => void;
    syncWithProjData(fields: string[], compute: (values: any[], keys: string[]) => ProjDataSyncTargets<this>): () => void;
    syncWithProjData(compute: (appData: Record<string, any>) => ProjDataSyncTargets<this>): () => void;
    syncWithProjData(targetOrCompute: any, compute?: any): () => void {
      return this._trackBinding((bindElToProjData as any)(this, targetOrCompute, compute));
    }

    /**
     * Sugar over `syncWithProjData` for its commonest use by far: hide this
     * element when a check over one or more appData fields says so.
     *
     *   this.bindHidden('shapeCount', (count) => count <= 1);
     *   this.bindHidden(['svgPath', 'maskVectorized'], ([p, v]) => !(p && v));
     *
     * Auto-unbound on disconnect, same as `syncWithProjData`.
     */
    bindHidden(field: string, hiddenWhen: (value: any) => boolean): () => void;
    bindHidden(fields: string[], hiddenWhen: (values: any[]) => boolean): () => void;
    bindHidden(fieldOrFields: any, hiddenWhen: (values: any) => boolean): () => void {
      return (this.syncWithProjData as any)(fieldOrFields,
        (values: any) => ({ methods: { hide: hiddenWhen(values) } }));
    }

    /**
     * Sugar over `syncWithProjData` for driving attributes instead of methods.
     * Return `null` for an attribute to remove it, `''` to set a bare boolean
     * attribute (see `ProjDataSyncTargets`).
     *
     *   this.bindAttrs('croppedImgUrl', (url) => ({ disabled: url ? null : '' }));
     *
     * Auto-unbound on disconnect, same as `syncWithProjData`.
     */
    bindAttrs(field: string, compute: (value: any) => Record<string, any>): () => void;
    bindAttrs(fields: string[], compute: (values: any[]) => Record<string, any>): () => void;
    bindAttrs(fieldOrFields: any, compute: (values: any) => Record<string, any>): () => void {
      return (this.syncWithProjData as any)(fieldOrFields,
        (values: any) => ({ attrs: compute(values) }));
    }

    /**
     * Binds Paramate params onto this element, the way syncWithProjData binds
     * appData. Three call shapes, the same ones params.subscribe takes:
     *
     *   this.bindParams('main:addHole', (p) => ({ methods: { hide: !p?.used } }));
     *   this.bindParams({ w: 'main:width' },
     *     ({ w }) => ({ attrs: { 'auto-min': w?.min, 'auto-max': w?.max } }));
     *   this.bindParams((params) => ({ attrs: { 'param-count': Object.keys(params).length } }));
     *
     * `compute` gets the param OBJECT, not just its value, so `.value`, `.min`,
     * `.max` and `.used` all come off the same thing. It runs once immediately
     * and again whenever the params it names change.
     *
     * Params don't exist until the configurator has initialised, so that first
     * run usually sees undefined — write `compute` to cope with it.
     *
     * Read-only. To change a param, call setParamValue() on <as-configurator>:
     * it owns pending state, undo history and the `trcad-param` input registry.
     *
     * Auto-unbound on disconnect, same as syncWithProjData.
     */
    bindParams(name: string, compute: (param: ParamData | undefined, name: string) => ProjDataSyncTargets<this>): () => void;
    bindParams<T extends ParamAliases>(aliases: T, compute: (params: ResolvedParams<T>) => ProjDataSyncTargets<this>): () => void;
    bindParams(compute: (params: Record<string, ParamData>) => ProjDataSyncTargets<this>): () => void;
    bindParams(targetOrCompute: any, compute?: any): () => void {
      return this._trackBinding((bindElToParams as any)(this, targetOrCompute, compute));
    }

    /**
     * Binds the library's own system state onto this element, the way
     * syncWithProjData binds appData. Three call shapes:
     *
     *   this.bindSysData('busy', (busy) => ({ attrs: { disabled: busy ? '' : null } }));
     *   this.bindSysData(['projectName', 'projectDirty'],
     *     ([name, dirty]) => ({ attrs: { 'default-text': dirty ? `${name} *` : name } }));
     *   this.bindSysData((sys) => ({ methods: { hide: sys.phase !== 'ready' } }));
     *
     * Applied once immediately, then whenever the fields it names change.
     * Unlike params, sysData always has a value — the flags start false and the
     * identity fields start undefined, so the first run is safe to rely on.
     *
     * Read-only, and there is no write path at all: sysData reports what the
     * library is doing, so the way to change it is to make the library do
     * something else.
     *
     * Auto-unbound on disconnect, same as syncWithProjData.
     */
    bindSysData<K extends SysDataKey>(key: K, compute: (value: SysData[K], key: K) => ProjDataSyncTargets<this>): () => void;
    bindSysData<K extends SysDataKey>(keys: readonly K[], compute: (values: Array<SysData[K]>, keys: readonly K[]) => ProjDataSyncTargets<this>): () => void;
    bindSysData(compute: (sys: Readonly<SysData>) => ProjDataSyncTargets<this>): () => void;
    bindSysData(targetOrCompute: any, compute?: any): () => void {
      return this._trackBinding((bindElToSysData as any)(this, targetOrCompute, compute));
    }
  }
  return WithAsEl;
}

/**
 * Same binding as `AsEl.syncWithProjData`, but for a plain `HTMLElement` you
 * already have a reference to — no custom-element class/tag needed. Useful
 * for static HTML groups (e.g. a `<div id="foo">` placed directly in an
 * app's index.html) that just need their visibility/attrs driven by
 * appData, without promoting them to their own component.
 *
 * `methods: { hide: value }` works the same as on a real `AsEl` — if `el`
 * doesn't already have its own `.hide()` (i.e. it isn't itself an AsEl
 * instance), one is attached automatically the first time this is called.
 * It sets and clears an inline `display`, so it can't lift a `hidden` class
 * the markup put on the element — bind `classes: { hidden }` for that.
 *
 *   bindElToProjData('#refine-shapes-group', 'shapeCount',
 *     (count) => ({ methods: { hide: count <= 1 } }));
 *
 * `el` is the element or a CSS selector for it, looked up once when the
 * binding is made (the same as `sendEv`). A null/undefined `el` is a no-op
 * returning a no-op unbind, so a plain `querySelector` result can still be
 * passed straight in without a `!`. It does `devlog` a warning though (dev
 * builds only): a binding that silently never fires is miserable to debug,
 * and a mistyped selector is the likeliest cause. Same line `wcQry` draws —
 * quiet on an ordinary no-match, noisy when it probably means a bug at the
 * call site.
 *
 * If the element is legitimately absent sometimes — markup the app renders
 * conditionally — pass `{ failSilently: true }` to skip the warning, so it
 * stays meaningful everywhere else. That also documents the intent at the
 * call site.
 *
 * Unlike `syncWithProjData`, cleanup isn't automatic (a plain element has no
 * disconnectedCallback to hook) — store and call the returned unbind function
 * yourself, typically from whatever component/module owns `el`'s lifecycle.
 */
export function bindElToProjData(el: BindTarget, field: string, compute: (value: any, key: string) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToProjData(el: BindTarget, fields: string[], compute: (values: any[], keys: string[]) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToProjData(el: BindTarget, compute: (appData: Record<string, any>) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToProjData(
  el: BindTarget,
  targetOrCompute: any,
  computeOrOpts?: any,
  maybeOpts?: BindElOpts,
): () => void {
  return bindElToSource(PROJ_DATA_SOURCE, el, targetOrCompute, computeOrOpts, maybeOpts);
}

/**
 * `bindElToProjData` narrowed to the case it's used for most: hide a plain
 * element when a check over one or more appData fields says so.
 *
 *   bindHidden('#offset-group', ['maskVectorized', 'svgPath'],
 *     ([vectorized, path]) => !(vectorized || path));
 *
 * The AsEl-scoped `this.bindHidden()` is the same thing with automatic
 * cleanup; this form returns an unbind you must call yourself. Null-safe and
 * takes the same `{ failSilently }` option as `bindElToProjData`.
 */
export function bindHidden(el: BindTarget, field: string, hiddenWhen: (value: any) => boolean, opts?: BindElOpts): () => void;
export function bindHidden(el: BindTarget, fields: string[], hiddenWhen: (values: any[]) => boolean, opts?: BindElOpts): () => void;
export function bindHidden(el: BindTarget, fieldOrFields: any, hiddenWhen: (values: any) => boolean, opts?: BindElOpts): () => void {
  return (bindElToProjData as any)(el, fieldOrFields,
    (values: any) => ({ methods: { hide: hiddenWhen(values) } }), opts);
}

/**
 * `bindElToProjData` narrowed to driving attributes on a plain element.
 * Return `null` for an attribute to remove it, `''` to set a bare boolean
 * attribute (see `ProjDataSyncTargets`).
 *
 *   bindAttrs('#continue-button', 'croppedImgUrl',
 *     (url) => ({ disabled: url ? null : '' }));
 *
 * Null-safe and takes the same `{ failSilently }` option as `bindElToProjData`.
 */
export function bindAttrs(el: BindTarget, field: string, compute: (value: any) => Record<string, any>, opts?: BindElOpts): () => void;
export function bindAttrs(el: BindTarget, fields: string[], compute: (values: any[]) => Record<string, any>, opts?: BindElOpts): () => void;
export function bindAttrs(el: BindTarget, fieldOrFields: any, compute: (values: any) => Record<string, any>, opts?: BindElOpts): () => void {
  return (bindElToProjData as any)(el, fieldOrFields, (values: any) => ({ attrs: compute(values) }), opts);
}

/**
 * Same binding as `AsEl.bindParams`, but for a plain `HTMLElement` you already
 * have a reference to — the param-side counterpart of `bindElToProjData`.
 *
 *   bindElToParams('#hole-options', 'main:addHole',
 *     (p) => ({ methods: { hide: !p?.used } }));
 *
 *   // or, when the markup already hides it with a class:
 *   bindElToParams('#box-info', 'main:baseShape',
 *     (p) => ({ classes: { hidden: Number(p?.value) !== 1 } }));
 *
 * Takes the same three shapes as params.subscribe (one name, an alias object,
 * or a bare compute for the whole table), and `compute` gets the param objects
 * rather than bare values. Applied once immediately, then on every change.
 *
 * Null-safe, with the same `{ failSilently }` option as `bindElToProjData`, and
 * it attaches a `.hide()` if the element hasn't got one. Cleanup is NOT
 * automatic — keep the returned unbind and call it from whatever owns `el`.
 */
export function bindElToParams(el: BindTarget, name: string, compute: (param: ParamData | undefined, name: string) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToParams<T extends ParamAliases>(el: BindTarget, aliases: T, compute: (params: ResolvedParams<T>) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToParams(el: BindTarget, compute: (params: Record<string, ParamData>) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToParams(
  el: BindTarget,
  targetOrCompute: any,
  computeOrOpts?: any,
  maybeOpts?: BindElOpts,
): () => void {
  return bindElToSource(PARAMS_SOURCE, el, targetOrCompute, computeOrOpts, maybeOpts);
}

/**
 * Same binding as `AsEl.bindSysData`, but for a plain `HTMLElement` you already
 * have a reference to — the sysData counterpart of `bindElToProjData`.
 *
 *   bindElToSysData('#spinner', 'busy',
 *     (busy) => ({ methods: { hide: !busy } }));
 *
 * Takes the same three shapes as sysData.subscribe (one key, an array of
 * keys, or a bare compute for the whole state). Applied once immediately, then
 * on every change.
 *
 * Null-safe, with the same `{ failSilently }` option as `bindElToProjData`, and
 * it attaches a `.hide()` if the element hasn't got one. Cleanup is NOT
 * automatic — keep the returned unbind and call it from whatever owns `el`.
 */
export function bindElToSysData<K extends SysDataKey>(el: BindTarget, key: K, compute: (value: SysData[K], key: K) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToSysData<K extends SysDataKey>(el: BindTarget, keys: readonly K[], compute: (values: Array<SysData[K]>, keys: readonly K[]) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToSysData(el: BindTarget, compute: (sys: Readonly<SysData>) => ProjDataSyncTargets<any>, opts?: BindElOpts): () => void;
export function bindElToSysData(
  el: BindTarget,
  targetOrCompute: any,
  computeOrOpts?: any,
  maybeOpts?: BindElOpts,
): () => void {
  return bindElToSource(SYS_DATA_SOURCE, el, targetOrCompute, computeOrOpts, maybeOpts);
}

/**
 * Sets a child `<as-text>`'s dict-key/fallback text. Always safe to call
 * repeatedly (e.g. once from connectedCallback right after a child as-text
 * already resolved its translation synchronously, and again later from
 * attributeChangedCallback — or, for elements built from static markup with
 * dict-key already present, from the extra attributeChangedCallback replay
 * custom-element upgrade does for pre-existing attributes): as-text tracks
 * whether it's currently showing a resolved translation and only applies
 * `default-text` when it isn't, so this can never stomp one. Never set
 * `textContent`/`innerText` on an as-text directly — go through this instead.
 */
export function applyLocalizedText(el: HTMLElement, dictKey: string | null | undefined, defaultText: string): void {
  el.setAttribute('default-text', defaultText);
  if (dictKey) el.setAttribute('dict-key', dictKey);
  else el.removeAttribute('dict-key');
}

/**
 * Sets/clears the `vars` attribute (placeholder values for {0}, {1}, ... in
 * the resolved text or default-text fallback) on a child `<as-text>`. Call
 * before applyLocalizedText — vars must be in place before dict-key so
 * they're picked up when the key subscription registers.
 */
export function applyLabelVars(el: HTMLElement, vars: any[] | null | undefined): void {
  if (vars) el.setAttribute('vars', JSON.stringify(vars));
  else el.removeAttribute('vars');
}

/**
 * Base class for new components.
 * - One place to set the tag: `static readonly tag`
 * - Not a retrofit — new components opt in
 * - Composes with other mixins: `class Foo extends autoAttrs(AsEl, SPECS)`
 * - For a component that composes several mixins (e.g. AsApp), use
 *   AsElMixin directly instead of extending AsEl — see its docs above.
 *
 * Usage:
 *   export class AsImportGroup extends AsEl {
 *     static readonly tag = 'as-import-group' as const;
 *     ...
 *   }
 *   AsImportGroup.define();
 *
 *   const group = wcQry(AsImportGroup);           // free function, whole document
 *   group?.wcQry(AsFileInput);                     // scoped to inside group
 *   someLooselyTypedRef.typed.subheadingDefaultText;  // tag-checked self-reference
 *
 * Why there's no "forgot to set tag" check at construction time: `new
 * Subclass()` throws a native "Illegal constructor" error unless the class
 * was already passed to customElements.define(), and
 * customElements.define('', Subclass) itself throws a native "not a valid
 * custom element name" error before any instance exists. So by the time an
 * AsEl constructor body could ever run, `tag` is already guaranteed to have
 * been a valid, non-empty string.
 */
export abstract class AsEl extends AsElMixin(HTMLElement) {}
